import React, { useMemo } from 'react';

import { inverseGoldenRatio } from '@glass/common/modules/goldenRatio/goldenRatio';
import Avatar from '@glass/web/components/base/Avatar';
import Card from '@glass/web/components/base/Card';
import CardContent from '@glass/web/components/base/CardContent';
import CardHeader from '@glass/web/components/base/CardHeader';
import Chip from '@glass/web/components/base/Chip';
import Skeleton from '@glass/web/components/base/Skeleton';
import Typography from '@glass/web/components/base/Typography';
import FormatQuote from '@glass/web/components/base/icons/FormatQuote';
import StarRounded from '@glass/web/components/base/icons/StarRounded';
import stringToColor from '@glass/web/modules/color/stringToColor';
import { MD } from '@glass/web/modules/theme/breakpointKeys';
import { CENTER, LEFT } from '@glass/web/modules/theme/constants';
import { FLEX, COLUMN, SPACE_BETWEEN, FLEX_END } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

// todo: not all these styles are necessary, remove
const useStyles = makeStyles<Pick<TestimonialProps, 'firstName'>>()((theme, { firstName }) => ({
  root: {
    gap: theme.spacing(1),
    width: '100%',
    height: '100%',
    padding: theme.spacing(0, 0.5),
  },
  avatar: {
    width: '30px',
    height: '30px',
    filter: 'saturate(40%) brightness(1)',
    backgroundColor: firstName ? stringToColor(firstName) : theme.palette.grey[900],
  },
  wrapper: {
    height: '100%',
    width: '100%',
    color: theme.palette.grey[900],
    display: FLEX,
    flexDirection: COLUMN,
    alignItems: LEFT,
    justifyContent: SPACE_BETWEEN,
    transition: theme.transitions.create(['background', 'font']),
  },
  avatarName: {
    display: FLEX,
    flexDirection: 'row',
    alignItems: CENTER,
    justifyContent: FLEX_END,
    gap: theme.spacing(1),
  },
  headerWrapper: {
    display: FLEX,
    flexDirection: 'row',
    alignItems: CENTER,
    textAlign: CENTER,
    justifyContent: SPACE_BETWEEN,
    gap: theme.spacing(1),
  },
  quoteWrapper: {
    height: '100%',
    display: FLEX,
    flexDirection: COLUMN,
    alignItems: LEFT,
    justifyContent: LEFT,
  },
  quote: {
    padding: theme.spacing(0, 2),
    flexWrap: 'wrap',
    margin: '0 auto',
    display: '-webkit-box',
    overflow: 'hidden',
    alignItems: CENTER,
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    justifyContent: CENTER,
    textAlign: LEFT,
    [theme.breakpoints.down(MD)]: {
      textAlign: CENTER,
      fontSize: '1.2rem',
    },
  },
  container: {
    padding: theme.spacing(0, 2),
  },
  endQuoteIcon: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  content: {
    display: FLEX,
    flexDirection: COLUMN,
    justifyContent: SPACE_BETWEEN,
    height: '100%',
  },
}));
export interface TestimonialProps {
  date?: string;
  id?: string;
  firstName: string;
  lastName: string;
  rating: number;
  quote: string;
  isLoading?: boolean;
}

function Testimonial({ firstName, lastName, quote, rating, isLoading }: TestimonialProps) {
  const { classes } = useStyles({ firstName });

  const name = useMemo(
    () => (lastName?.length > 0 ? `${firstName} ${lastName.charAt(0)}.` : firstName),
    [firstName, lastName],
  );

  const headerText = useMemo(
    () => (
      <div className={classes.headerWrapper}>
        <div className={classes.avatarName}>
          <Avatar aria-label="image" className={classes.avatar}>
            {name?.length > 0 ? name.charAt(0).toUpperCase() : null}
          </Avatar>
          {!name && isLoading ? <Skeleton width={50} /> : name}
        </div>
        <Chip
          icon={<StarRounded color="secondary" />}
          label={
            <Typography color="secondary" variant="h6" fontWeight={700}>
              {rating || 5}
            </Typography>
          }
        />
      </div>
    ),
    [classes.headerWrapper, classes.avatarName, classes.avatar, name, isLoading, rating],
  );

  return (
    <div className={classes.root}>
      <Card variant="outlined" className={classes.wrapper}>
        <CardContent className={classes.content}>
          <FormatQuote
            fontSize="medium"
            sx={{
              transform: 'rotateY(180deg)',
            }}
          />

          {!quote && isLoading ? (
            <div className={classes.container}>
              <Skeleton width="100%" />
              <Skeleton width="100%" />
              <Skeleton width="100%" />
              <Skeleton width={`${inverseGoldenRatio}%`} />
            </div>
          ) : null}
          {quote ? (
            <Typography
              display={FLEX}
              fontWeight={500}
              className={classes.quote}
              component="span"
              fontStyle="italic"
              textOverflow="ellipsis"
              flexWrap="wrap"
            >
              {quote}
            </Typography>
          ) : null}

          <div className={classes.endQuoteIcon}>
            <FormatQuote className={classes.endQuoteIcon} fontSize="medium" />
          </div>
        </CardContent>
        <CardHeader
          className={classes.headerWrapper}
          titleTypographyProps={{
            variant: 'body2',
            fontWeight: 600,
          }}
          title={headerText}
        />
      </Card>
    </div>
  );
}

export default Testimonial;
